import { useT } from '@transifex/react';
import { useEffect, type JSX } from 'react';
import {
	Controller,
	useFormContext,
	type ControllerFieldState,
	type ControllerRenderProps,
} from 'react-hook-form';

import {
	type Attachment,
	type AttachmentType,
} from 'pkg/api/models/attachment';
import { getTypeTranslation } from 'pkg/api/models/attachment/fmt';

import useAttachments from 'components/attachment/hooks/useAttachments';
import * as Input from 'components/form/inputs';
import Column from 'components/layout/column';

interface AttachmentFieldProps {
	name: string;
	required?: boolean;
	readonly?: boolean;
	attachmentType?: AttachmentType;
	attachments?: Attachment[];
	maxAttachments?: number;
	onUploadAttachment?: (attachment: Attachment) => void;
	onRemoveAttachment?: (attachmentId: number) => Promise<boolean>;
}

interface ContentProps {
	fieldState: ControllerFieldState;
	controllerField: ControllerRenderProps;
	attachments: Attachment[];
	FileUploader: JSX.Element;
	AttachmentItems: JSX.Element;
}

function Content({
	fieldState,
	controllerField,
	attachments,
	FileUploader,
	AttachmentItems,
}: ContentProps) {
	useEffect(() => {
		const ids = attachments.map((a) => a.id);
		controllerField.onChange(ids);
	}, [attachments]);

	return (
		<Column>
			{attachments.length === 0 && FileUploader}
			{AttachmentItems}
			{fieldState.error && (
				<Input.InfoText error>
					{fieldState.error.message || 'This field is required'}
				</Input.InfoText>
			)}
		</Column>
	);
}

export function AttachmentField({
	name,
	required = false,
	readonly = false,
	attachmentType,
	attachments = [],
	maxAttachments,
	onUploadAttachment,
	onRemoveAttachment,
}: AttachmentFieldProps) {
	const { control } = useFormContext();
	const {
		attachments: hookAttachments,
		FileUploader,
		AttachmentItems,
	} = useAttachments({
		attachments,
		readonly,
		maxAttachments,
		type: attachmentType,
		onNewAttachment: onUploadAttachment,
		onRemoveAttachment: onRemoveAttachment,
	});
	const t = useT();

	const validate = (value: number[]) => {
		if (required && (!Array.isArray(value) || value.length === 0)) {
			return t('This field is required');
		}

		if (
			attachmentType &&
			Array.isArray(hookAttachments) &&
			hookAttachments.length > 0
		) {
			const allMatch = hookAttachments.every(
				(att) => att.type === attachmentType
			);

			if (!allMatch) {
				return t('All attachments must be of type {attachmentTypeString}', {
					attachmentTypeString: getTypeTranslation(attachmentType),
				});
			}
		}

		return true;
	};

	return (
		<Controller
			name={name}
			control={control}
			defaultValue={[]}
			rules={{
				validate,
			}}
			render={({ field: controllerField, fieldState }) => (
				<Content
					controllerField={controllerField}
					fieldState={fieldState}
					attachments={hookAttachments}
					AttachmentItems={AttachmentItems}
					FileUploader={FileUploader}
				/>
			)}
		/>
	);
}
